import type { ShotView } from "@dramapie/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  OVERLAY_ALPHA,
  diffFrames,
  dialogueChars,
  frameCandidates,
  paintDiff,
  suggestedDuration,
  type DiffStats,
} from "../../lib/frames";
import { useImageViewer } from "../media/ImageViewer";

export interface FrameConfirmDialogProps {
  open: boolean;
  shot: ShotView | null;
  shotLabel: string;
  onClose: () => void;
  /** 通过 / 重抽：交给页面去发命令（组件不直接写文件）。 */
  onApprove: (shotId: string) => void;
  onRegenerate: (shotId: string) => void;
}

type Mode = "single" | "overlay" | "diff";

/**
 * 首尾帧 · 确认（Wiki F-01 ~ F-07）。
 *
 * 核心不是「并排看两张」，而是「找出变化」：叠加看位移，差异看穿帮。
 * 并排放两张时，人眼很难发现肩膀挪了 3 个像素；叠加和像素差能一眼看出来。
 */
export function FrameConfirmDialog({
  open,
  shot,
  shotLabel,
  onClose,
  onApprove,
  onRegenerate,
}: FrameConfirmDialogProps) {
  const frames = useMemo(() => (shot ? frameCandidates(shot) : []), [shot]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mode, setMode] = useState<Mode>("single");
  const [stats, setStats] = useState<DiffStats | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageViewer = useImageViewer();
  const first = frames[0];
  const second = frames[1];
  const canCompare = Boolean(first && second);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
    setMode("single");
    setStats(null);
  }, [open, shot?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  /** 叠加 / 差异都画到同一张 canvas 上。 */
  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !first?.url || !second?.url) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const [a, b] = await Promise.all([loadImage(first.url), loadImage(second.url)]);
    if (!a || !b) return;
    canvas.width = a.width;
    canvas.height = a.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (mode === "overlay") {
      ctx.globalAlpha = 1;
      ctx.drawImage(a, 0, 0);
      ctx.globalAlpha = OVERLAY_ALPHA;
      ctx.drawImage(b, 0, 0);
      ctx.globalAlpha = 1;
      return;
    }

    ctx.drawImage(a, 0, 0);
    const left = ctx.getImageData(0, 0, canvas.width, canvas.height);
    ctx.drawImage(b, 0, 0);
    const right = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const result = diffFrames(left, right);
    setStats(result);
    ctx.putImageData(paintDiff(left, right), 0, 0);
  }, [first?.url, second?.url, mode]);

  useEffect(() => {
    if (!open || mode === "single") {
      setStats(null);
      return;
    }
    void draw();
  }, [open, mode, draw]);

  if (!open || !shot) return null;

  const active = frames[Math.min(activeIndex, Math.max(0, frames.length - 1))];
  const chars = dialogueChars(shot.dialogue);
  const suggested = suggestedDuration(chars);
  const checks = shot.checks ?? [];
  const passed = checks.filter((c) => c.pass).length;

  return (
    <div className="dp-scrim" role="presentation" onClick={onClose}>
      <div
        className="dp-dialog dp-frame-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`${shotLabel} 首尾帧确认`}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="dp-sheet-head">
          <div>
            <p className="dp-sheet-kicker">首尾帧 · 确认</p>
            <h2 className="dp-sheet-title">{shotLabel}</h2>
            <p className="dp-sheet-sub">
              {shot.framing} · {shot.duration}s · {frames.length} 帧
            </p>
          </div>
          <div className="dp-frame-tools">
            {frames.map((frame, index) => (
              <button
                key={frame.role}
                type="button"
                className="dp-seg-btn"
                aria-pressed={mode === "single" && activeIndex === index}
                onClick={() => {
                  setMode("single");
                  setActiveIndex(index);
                }}
              >
                {frame.label}
              </button>
            ))}
            {canCompare ? (
              <>
                <button
                  type="button"
                  className="dp-seg-btn"
                  aria-pressed={mode === "overlay"}
                  onClick={() => setMode("overlay")}
                >
                  叠加
                </button>
                <button
                  type="button"
                  className="dp-seg-btn"
                  aria-pressed={mode === "diff"}
                  onClick={() => setMode("diff")}
                >
                  差异
                </button>
              </>
            ) : null}
          </div>
        </header>

        <div className="dp-frame-body">
          <div className="dp-frame-stage">
            {frames.length === 0 ? (
              <div className="dp-frame-empty">
                <p>这个镜头还没有首帧。</p>
                <button type="button" className="dp-btn sm primary" onClick={() => onRegenerate(shot.id)}>
                  生成首帧
                </button>
              </div>
            ) : mode === "single" ? (
              <>
                {/* 点大图仍然切首 / 尾（原行为）；放大镜按钮进全屏看图，不抢这个点击。 */}
                <button
                  type="button"
                  className="dp-frame-image-btn"
                  title={frames.length > 1 ? "点击切换首帧 / 尾帧" : undefined}
                  aria-label={frames.length > 1 ? "切换首帧与尾帧" : active?.label}
                  onClick={() => {
                    if (frames.length > 1) setActiveIndex((index) => (index + 1) % frames.length);
                  }}
                >
                  {active?.url ? <img src={active.url} alt={active.label} /> : null}
                </button>
                {active?.url ? (
                  <button
                    type="button"
                    className="dp-btn sm dp-frame-zoom"
                    aria-label="放大查看这一帧"
                    title="全屏看图：长按放大镜、滚轮缩放"
                    onClick={() =>
                      imageViewer.open(
                        frames
                          .filter((frame) => frame.url)
                          .map((frame) => ({
                            src: frame.url as string,
                            title: `${shotLabel} · ${frame.label}`,
                            subtitle: frame.label,
                          })),
                        Math.max(
                          0,
                          frames.filter((frame) => frame.url).findIndex((frame) => frame.role === active.role),
                        ),
                      )
                    }
                  >
                    放大
                  </button>
                ) : null}
              </>
            ) : (
              <canvas ref={canvasRef} className="dp-frame-canvas" aria-label={mode === "overlay" ? "叠加对比" : "像素差异"} />
            )}
            {mode === "diff" && stats ? (
              <p className="dp-frame-stat">
                变化像素 <b>{stats.changed.toLocaleString("zh-CN")}</b> / {stats.total.toLocaleString("zh-CN")}
                <em>{(stats.ratio * 100).toFixed(1)}%</em>
              </p>
            ) : null}
            {mode === "overlay" ? <p className="dp-frame-stat">两张各 50% 叠在一起，位移会重影</p> : null}
          </div>

          <aside className="dp-frame-side">
            <section>
              <h3>审核信息</h3>
              <dl className="dp-kv">
                <div>
                  <dt>建议片长</dt>
                  <dd>{suggested}s</dd>
                </div>
                <div>
                  <dt>当前时长</dt>
                  <dd>{shot.duration}s</dd>
                </div>
                <div>
                  <dt>帧数</dt>
                  <dd>{frames.length}</dd>
                </div>
                <div>
                  <dt>台词字数</dt>
                  <dd>{chars}</dd>
                </div>
                <div>
                  <dt>尾帧来源</dt>
                  <dd>{shot.endFrame ? "已生成" : "未生成"}</dd>
                </div>
                <div>
                  <dt>已花点数</dt>
                  <dd>{shot.costPoints} 点</dd>
                </div>
              </dl>
            </section>

            <section>
              <h3>
                确认项 <span className="dp-tiny-note">{passed}/{checks.length || 5}</span>
              </h3>
              {checks.length === 0 ? (
                <p className="dp-tiny-note">还没有自动检查结果（生成后由服务端写入）。</p>
              ) : (
                <ul className="dp-check-list">
                  {checks.map((check, index) => (
                    <li key={index} data-pass={check.pass ? "true" : "false"}>
                      <span aria-hidden="true">{check.pass ? "✓" : "×"}</span>
                      {check.q}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="dp-frame-actions">
              <button type="button" className="dp-btn primary" onClick={() => onApprove(shot.id)}>
                通过
              </button>
              <button type="button" className="dp-btn" onClick={() => onRegenerate(shot.id)}>
                重抽
              </button>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}
